/**
 * Bridge Assistant — curated product help (V1: guided answers, no site-wide AI chat).
 */
import { FEATURE_GUIDE_SECTIONS } from './featureGuideContent';

export const BRIDGE_ASSISTANT_INTRO_KEY = 'mlb-bridge-assistant-intro-dismissed';

export const QUICK_PROMPTS = [
  {
    id: 'what-is-story-studio',
    label: 'What is AI Story Studio?',
    answer:
      'AI Story Studio is where you capture memories by voice, text or guided prompts, then shape them into chapters and Legacy Books. Storybook Mode is the guided prompt journey; Stories & voice is for freeform memories. AI helps organise and polish what you provide — you review before anything is exported.',
    actions: [
      { label: 'Open Story Studio', route: '/story-studio' },
      { label: 'Feature Guide', route: '/guide', guideSection: 'ai-story-studio' },
    ],
  },
  {
    id: 'what-is-start-for-me',
    label: 'What is Start for me?',
    answer:
      'Start for me gives you one memory prompt, a few follow-up questions, and lets you write, record, save or try another prompt.',
    actions: [
      { label: 'Open Story Studio', route: '/story-studio?startForMe=1#start-for-me' },
      { label: 'Feature Guide', route: '/guide', guideSection: 'ai-story-studio' },
    ],
  },
  {
    id: 'what-is-handover-vault',
    label: 'What is the Family Handover Vault?',
    answer:
      'Family Handover Vault is a private place to organise important contacts, document locations, account instructions, final messages and people to notify. It helps your family find practical information — private by default, and not a substitute for a will or professional advice.',
    actions: [
      { label: 'Open Vault', route: '/vault' },
      { label: 'Feature Guide', route: '/guide', guideSection: 'family-handover-vault' },
    ],
  },
  {
    id: 'vault-legal-advice',
    label: 'Is the Vault legal advice?',
    answer:
      'No. The Family Handover Vault is a private organisation tool only. It does not replace a will, solicitor, attorney, executor or formal estate planning. Use formal legal documents and professional advice where needed.',
    actions: [
      { label: 'Open Vault', route: '/vault' },
      { label: 'Feature Guide', route: '/guide', guideSection: 'family-handover-vault' },
    ],
  },
  {
    id: 'vault-what-first',
    label: 'What should I add first?',
    answer:
      'Start with one practical note — an important contact, where a key document is stored, or a short instruction about an account. Add more over time and set a review reminder so your vault stays up to date.',
    actions: [
      { label: 'Open Vault', route: '/vault' },
      { label: 'Set a reminder', resolve: 'reminders' },
    ],
  },
  {
    id: 'vault-passwords',
    label: 'Can I store passwords here?',
    answer:
      'No — do not store raw passwords in the Vault. For security, use a password manager and note where your family can find access instructions instead.',
    actions: [
      { label: 'Open Vault', route: '/vault' },
      { label: 'Feature Guide', route: '/guide', guideSection: 'family-handover-vault' },
    ],
  },
  {
    id: 'vault-who-sees',
    label: 'Who can see my Vault?',
    answer:
      'In V1, vault content is private to you — the logged-in owner only. It is not shown on public memorial pages, family tree pages, or Legacy Book PDFs by default. Collaborators cannot see vault items yet.',
    actions: [
      { label: 'Open Vault', route: '/vault' },
      { label: 'Privacy & trust', route: '/guide', guideSection: 'privacy-trust' },
    ],
  },
  {
    id: 'record-memory',
    label: 'How do I record a memory?',
    answer:
      'Open a person profile → Stories & voice → Record voice memory. You can also record answers inside Storybook Mode prompts. Transcribe when you want editable text.',
    actions: [
      { label: 'Open Stories & voice', resolve: 'personStories' },
      { label: 'Open Storybook', resolve: 'storybook' },
    ],
  },
  {
    id: 'voice-to-story',
    label: 'How do I turn a voice note into a story?',
    answer:
      'After recording, use Transcribe to text in Stories & voice or Storybook prompts. Then use Polish into narrative to shape the transcript — review and accept before it becomes final.',
    actions: [
      { label: 'Stories & voice', resolve: 'personStories' },
      { label: 'Feature Guide', route: '/guide', guideSection: 'stories-voice' },
    ],
  },
  {
    id: 'chapter-draft',
    label: 'How do I create a chapter draft?',
    answer:
      'In Storybook Mode, complete at least three answers in a chapter, then use Create chapter draft in the sidebar. AI organises your answers into a draft chapter — review and edit before it appears in your Legacy Book.',
    actions: [
      { label: 'Open Storybook', resolve: 'storybook' },
      { label: 'Feature Guide', route: '/guide', guideSection: 'storybook' },
    ],
  },
  {
    id: 'export-legacy-book',
    label: 'How do I export a Legacy Book?',
    answer:
      'Use Preview & export in your archive sidebar, or Archive settings → Exports → Keepsake PDF. Review completed stories and chapter drafts first — drafts are not included until you approve them.',
    actions: [
      { label: 'Archive settings', resolve: 'treeSettings' },
      { label: 'Feature Guide', route: '/guide', guideSection: 'exports' },
    ],
  },
  {
    id: 'voice-memory',
    label: 'How do I add a voice memory?',
    answer:
      'Open a person profile, go to Stories & voice, then choose Record voice memory. You can transcribe it later if you want editable text.',
    actions: [
      { label: 'Open Stories & voice', resolve: 'personStories' },
      { label: 'Open Feature Guide', route: '/guide', guideSection: 'stories-voice' },
    ],
  },
  {
    id: 'what-is-memorial-bridge',
    label: 'What is Memorial Bridge?',
    answer:
      'Memorial Bridge is Pillar 3 of MyLegacyBridge — a respectful remembrance space built from memories you already preserve. Create a private memorial draft, choose what to share, and publish a tribute page only when your family is ready.',
    actions: [
      { label: 'Open Memorials', resolve: 'memorials' },
      { label: 'Feature Guide', route: '/guide', guideSection: 'memorial-bridge' },
    ],
  },
  {
    id: 'memorial-page',
    label: 'How do I create a memorial page?',
    answer:
      'Open Memorials or a person profile, enable a private memorial draft, then add life story, photos, tributes settings and appearance. Preview before publishing — nothing is public until you choose.',
    actions: [
      { label: 'Open Memorials', resolve: 'memorials' },
      { label: 'Feature Guide', route: '/guide', guideSection: 'memorial-bridge' },
    ],
  },
  {
    id: 'memorial-tributes',
    label: 'How do public tributes work?',
    answer:
      'Visitors can leave tribute messages on published memorial pages when you enable them. New tributes are pending by default — you approve, hide or mark spam in the memorial workspace. Email addresses are never shown publicly.',
    actions: [
      { label: 'Open Memorials', resolve: 'memorials' },
      { label: 'Memorial guide', route: '/guide', guideSection: 'memorials' },
    ],
  },
  {
    id: 'memorial-qr',
    label: 'How do QR keepsakes work?',
    answer:
      'After you publish a public memorial, Memorial Bridge creates a dynamic QR link that points to your tribute page — not private archive pages. You can download the QR for services or keepsakes; the same code stays valid if you update the page.',
    actions: [
      { label: 'Open Memorials', resolve: 'memorials' },
      { label: 'Feature Guide', route: '/guide', guideSection: 'memorial-bridge' },
    ],
  },
  {
    id: 'memorial-private',
    label: 'Can I keep a memorial private?',
    answer:
      'Yes. Memorials are disabled or private by default. A private draft is only visible to archive editors until you publish. Unpublishing removes public access — visitors see a generic unavailable page, not private archive details.',
    actions: [
      { label: 'Open Memorials', resolve: 'memorials' },
      { label: 'Privacy & trust', route: '/guide', guideSection: 'privacy-trust' },
    ],
  },
  {
    id: 'memorial-music-video',
    label: 'Can I add music or videos?',
    answer:
      'Yes — add Memory Soundtrack links and external video URLs in the memorial workspace. Visitors open links manually; there is no autoplay. Uploading audio or video files is not required for V1.',
    actions: [
      { label: 'Open Memorials', resolve: 'memorials' },
      { label: 'Memorial guide', route: '/guide', guideSection: 'memorials' },
    ],
  },
  {
    id: 'memorial-donations',
    label: 'Can people donate through a memorial?',
    answer:
      'You can add an optional external support or charity link chosen by your family. MyLegacyBridge does not collect donations, process payments, or take a percentage — the link opens an external page.',
    actions: [
      { label: 'Open Memorials', resolve: 'memorials' },
      { label: 'Feature Guide', route: '/guide', guideSection: 'memorial-bridge' },
    ],
  },
  {
    id: 'legacy-book',
    label: 'How do I export a Legacy Book?',
    answer:
      'Use Preview & export in the sidebar, or Archive settings → Exports → Keepsake PDF. Review your archive readiness first if you like.',
    actions: [
      { label: 'Archive settings', resolve: 'treeSettings' },
      { label: 'Open Feature Guide', route: '/guide', guideSection: 'exports' },
    ],
  },
  {
    id: 'priceless-possession',
    label: 'How do I add a priceless possession?',
    answer:
      'On a person profile, open Overview or People, places & things, then add a priceless possession with a photo and the story behind it.',
    actions: [
      { label: 'Open person profile', resolve: 'personStories' },
      { label: 'Open Feature Guide', route: '/guide', guideSection: 'media-possessions' },
    ],
  },
  {
    id: 'invite-family',
    label: 'How do I invite family?',
    answer:
      'Open your archive sidebar → Invite family. Choose view or edit access. They accept by email — nothing is public by default.',
    actions: [
      { label: 'Invite family', resolve: 'treeMembers' },
      { label: 'Open Feature Guide', route: '/guide', guideSection: 'privacy-trust' },
    ],
  },
  {
    id: 'what-next',
    label: 'What should I do next?',
    answer: null,
    dynamic: 'whatNext',
  },
];

export function getWhatNextAnswer(ctx) {
  switch (ctx.page) {
    case 'dashboard':
      return {
        text: 'Create or open a family archive, add yourself as a person, then capture one story or voice memory. The dashboard checklist can guide you.',
        actions: [
          { label: 'Open dashboard', resolve: 'dashboard' },
          { label: 'Open Feature Guide', route: '/guide' },
        ],
      };
    case 'tree':
      return {
        text: 'Add people to your tree, link relationships, then open a profile to write a story or record a voice memory.',
        actions: [
          { label: 'People tab', resolve: 'treePeople' },
          { label: 'Family tree', resolve: 'treeChart' },
        ],
      };
    case 'person':
      return {
        text: 'Complete the profile overview, add a story or voice memory, or preserve a document linked to this person.',
        actions: [
          { label: 'Stories & voice', resolve: 'personStories' },
          { label: 'Documents', resolve: 'personDocuments' },
        ],
      };
    case 'storybook':
      return {
        text: 'AI Story Studio helps you write or record guided answers, transcribe voice, create chapter drafts, and preview your Legacy Book. Pick one prompt to start.',
        actions: [
          { label: 'Open Story Studio', route: '/story-studio' },
          { label: 'Storybook guide', route: '/guide', guideSection: 'storybook' },
        ],
      };
    case 'memorial':
    case 'memorial-manage':
      return {
        text: 'Memorial Bridge lets you prepare a tribute page from preserved memories — private until you publish. Add life story, photos, tributes and music links, then preview before sharing. QR keepsakes are available after publish.',
        actions: [
          { label: 'Memorials', resolve: 'memorials' },
          { label: 'Memorial Bridge guide', route: '/guide', guideSection: 'memorial-bridge' },
        ],
      };
    case 'reminders':
      return {
        text: 'Add birthdays, remembrance days, or custom dates. You can preview digital tribute cards from saved reminders.',
        actions: [
          { label: 'Reminders', resolve: 'reminders' },
          { label: 'Reminders guide', route: '/guide', guideSection: 'reminders' },
        ],
      };
    default:
      return {
        text: 'Open the Feature Guide for a full map of MyLegacyBridge, or start from the dashboard with one person and one memory.',
        actions: [
          { label: 'Feature Guide', route: '/guide' },
          { label: 'Dashboard', resolve: 'dashboard' },
        ],
      };
  }
}

export function getContextGreeting(ctx) {
  switch (ctx.page) {
    case 'person':
      return 'Need help completing this profile?';
    case 'storybook':
      return 'Want to record your answer instead of typing?';
    case 'story-studio':
      return 'Ready to capture a memory or open Storybook Mode?';
    case 'vault':
      return 'Organising important contacts or document locations?';
    case 'memorial':
    case 'memorial-manage':
      return 'Need help publishing or adding tributes?';
    case 'tree':
      return 'Looking for something in this archive?';
    case 'dashboard':
      return 'Not sure where to start?';
    case 'reminders':
      return 'Setting up family reminders?';
    case 'settings':
      return 'Looking for account or privacy settings?';
    case 'dna':
      return 'Questions about DNA uploads or privacy?';
    default:
      return null;
  }
}

export function resolveAssistantRoute(action, ctx) {
  if (action.guideSection) {
    return `/guide#${action.guideSection}`;
  }
  if (action.route) {
    return action.route;
  }

  switch (action.resolve) {
    case 'dashboard':
      return '/dashboard';
    case 'reminders':
      return '/reminders';
    case 'storybook':
      return '/storybook';
    case 'storyStudio':
      return '/story-studio';
    case 'handoverVault':
      return '/vault';
    case 'memorials':
      return '/memorials';
    case 'dna':
      return '/dna';
    case 'settings':
      return '/settings';
    case 'guide':
      return '/guide';
    case 'faq':
      return '/faq';
    case 'treeChart':
      return ctx.treeBase ? `${ctx.treeBase}?tab=chart` : '/dashboard';
    case 'treePeople':
      return ctx.treeBase ? `${ctx.treeBase}?tab=grid` : '/dashboard';
    case 'treeMembers':
      return ctx.treeBase ? `${ctx.treeBase}/members` : '/dashboard';
    case 'treeSettings':
      return ctx.treeBase ? `${ctx.treeBase}?tab=settings` : '/dashboard';
    case 'treeReadiness':
      return ctx.treeBase ? `${ctx.treeBase}?tab=readiness` : '/dashboard';
    case 'personStories':
      return ctx.personHref ? `${ctx.personHref}?view=stories` : '/dashboard';
    case 'personDocuments':
      return ctx.personHref ? `${ctx.personHref}?view=documents` : '/dashboard';
    default:
      return '/guide';
  }
}

/** Flatten feature guide for simple search. */
export function searchFeatureGuide(query) {
  const q = (query || '').trim().toLowerCase();
  if (!q || q.length < 2) return [];

  const results = [];
  for (const section of FEATURE_GUIDE_SECTIONS) {
    for (const feature of section.features) {
      const haystack = [feature.name, feature.benefit, feature.howTo, section.title]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (haystack.includes(q)) {
        results.push({
          id: feature.id,
          name: feature.name,
          howTo: feature.howTo,
          route: feature.route,
          sectionId: section.id,
          status: feature.status,
        });
      }
    }
  }
  return results.slice(0, 6);
}

export function matchQuickPrompt(query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return null;
  return QUICK_PROMPTS.find((p) => p.label.toLowerCase().includes(q) || q.includes(p.label.toLowerCase().slice(0, 12)));
}
